import { apiFetch } from '@/api/client'
import { STOP_COPY } from '@/lib/status'
import { unstopped, type Account } from '@/data/types'

export type AggregateMeCaller = {
  sub: string
  email?: string
  preferredUsername?: string
  agentId?: string
  hierarchyLevel?: string
  roles: string[]
  azp?: string
  accessLevel?: string
  aggregateGroupId: string
  groupName: string
  memberList?: Array<{ _id: string; name: string; level: string }>
}

export type AggregateMeResponse = {
  ok: boolean
  caller: AggregateMeCaller
}

type StandardMeEnvelope = {
  status: number
  message: string
  data: AggregateMeResponse
}

/** CM-style session probe — JWT + aggregate_members attach on Aggregate-API. */
export async function fetchAggregateMe(): Promise<AggregateMeResponse> {
  const body = await apiFetch<StandardMeEnvelope | AggregateMeResponse>('/v1/me', {
    method: 'POST',
    body: JSON.stringify({}),
  })
  // Standard API Response from Aggregate-API; tolerate legacy flat shape in older mocks.
  if (body && typeof body === 'object' && 'data' in body && body.data && 'caller' in body.data) {
    return body.data
  }
  return body as AggregateMeResponse
}

/**
 * Console Account stub for Aggregate Keycloak sessions (no hierarchy agentId).
 * Lab Aggregate used the Aggregate profile, not `/v1/agents/{agentId}`.
 */
export function accountFromAggregateMe(caller: AggregateMeCaller): Account {
  const username =
    caller.preferredUsername?.trim() ||
    caller.email?.trim() ||
    caller.groupName?.trim() ||
    caller.aggregateGroupId
  const displayName = caller.groupName?.trim() || username
  const memberCount = caller.memberList?.length ?? 0
  return {
    id: caller.aggregateGroupId,
    username,
    displayName,
    // Closest ladder label for chrome; Aggregate is not an agent seat.
    role: 'company-master',
    parentId: null,
    path: [],
    status: 'active',
    createdAt: new Date().toISOString(),
    lastActiveAt: null,
    creditLimit: 0,
    balance: 0,
    distributedBalance: 0,
    exposure: 0,
    settlement: 0,
    share: { sportsbook: 0, casino: 0 },
    commission: { fancy: 0, bookmaker: 0, matchOdds: 0 },
    betLimits: { min: 0, max: 0 },
    games: {
      casino: true,
      cardGame: true,
      liveGame: true,
      sportsbook: true,
    },
    childCount: memberCount,
    memberCount,
    flags: [],
    ...unstopped('active', STOP_COPY.notWiredLive),
  }
}
